import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Container } from "react-bootstrap";
import ApiService from "../../../ApiService";
import MuiAlert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { getSingle } from "../../../strapi";
import { AiOutlineClose } from "react-icons/ai";
import { convertEpochToDate } from "../../../util/common";
import { clinicianIdFromRefreshAuth, getLoggedInPersonName } from "../../../util/authUtil";
import TextArea from "../../TextArea";
import ReactQuill from "react-quill";

import "react-quill/dist/quill.snow.css";

const MAX_NOTE_BYTES = 380 * 1024; // 380 KB
const INLINE_IMG_REGEX = /<img[^>]+src="data:image\//i;
const REQUIRED_FIELD_REGEX = /\{\{(.*?):required\}\}/;

const getByteSize = (str = "") => new Blob([str]).size;
const toKB = bytes => Math.ceil(bytes / 1024);

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const CLONE_HEADER = "<h2>Corrected patient intake form response below and highlighted</h2> <br>";
/**
 * @param {Object} props
 * @param {Object} props.details
 * @param {Function} props.handleSnackOpen
 * @param {Function} props.submitNotes
 * @param {Function} props.onHide
 * @param {Array<Object>} props.templates
 */
function CreateChartNotesModal({ show, onHide, patientDetails, referenceId, referenceType, chartDetails, cb, fetchChartNotes, isClone = false }) {
  const [chartingNoteId, setChartingNoteId] = useState(chartDetails?.id || null);
  const [chartDetail, setChartDetail] = useState(chartDetails || null);
  const [templatesList, setTemplatesList] = useState([]);

  const [messageText, setMessageText] = useState(chartDetails?.note || "");
  const [updatedMessageText, setUpdatedMessageText] = useState(chartDetails?.note || "");
  const [noteType, setNoteType] = useState(chartDetails?.noteType || "");
  const [noteVisibility, setNoteVisibility] = useState(chartDetails?.visibility || "INTERNAL_NOTE");

  const [isLoading, setIsLoading] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [error, setError] = useState({
    isMessage: false,
    isNoteType: false,
    message: ""
  });
  const [authorId, setAuthorId] = useState(null);
  const [authorName, setAuthorName] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");

  useEffect(() => {
    clinicianIdFromRefreshAuth().then(res => setAuthorId(res));
    getLoggedInPersonName().then(res => setAuthorName(res));
    const handleNewChartDetails = details => {
      setMessageText(details?.note);
      setUpdatedMessageText(details?.noteType === "PATIENT_QUESTIONNAIRE_RESPONSE" ? `${CLONE_HEADER} ${details?.note}` : details?.note);
      setNoteType(details?.noteType);
      setChartingNoteId(details?.id);
      setChartDetail(details);
      setNoteVisibility(details?.visibility);
    };

    if (chartDetails?.id) {
      handleNewChartDetails(chartDetails);
    } else {
      ApiService.getLatestChartNoteWithStatusDraft(referenceId, referenceType, patientDetails?.userId)
        .then(res => {
          if (res.data) {
            handleNewChartDetails(res.data);
          }
        })
        .catch(err => {
          console.error("Error fetching chart details:", err);
        });
    }
  }, [chartDetails, referenceId, referenceType, patientDetails, chartDetails?.id]);

  const [testType, setTestType] = useState("");
  const getTemplateFromCms = async () => {
    try {
      const content = await getSingle("clinician-portal", {
        populate: {
          chartingNoteTemplates: {
            populate: "*"
          }
        }
      });

      const response = await ApiService.getClinicianTemplates();

      const cmsTemplates =
        content?.chartingNoteTemplates?.map(item => ({
          ...item,
          description: item.content
        })) || [];

      const apiTemplates = response.status === 200 ? response.data : [];

      setTemplatesList([...templatesList, ...cmsTemplates, ...apiTemplates]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getTemplateFromCms();
  }, []);

  const saveOrReleaseNotes = async status => {
    // Required placeholders must be filled
    if (REQUIRED_FIELD_REGEX.test(updatedMessageText)) {
      setError(prev => ({ ...prev, isMessage: true }));
      setSnackSeverity("error");
      setSnackText("Please fill all the required fields");
      setSnackOpen(true);
      return;
    }

    // Reject inline base64 images
    if (INLINE_IMG_REGEX.test(updatedMessageText || "")) {
      setSnackSeverity("warning");
      setSnackText("Pasted images detected. Please remove images or upload them separately.");
      setSnackOpen(true);
      return;
    }

    // Enforce payload size cap
    const bytes = getByteSize(updatedMessageText);
    if (bytes > MAX_NOTE_BYTES) {
      setSnackSeverity("warning");
      setSnackText(`Note is too large (${toKB(bytes)} KB > ${toKB(MAX_NOTE_BYTES)} KB). Please remove images/formatting or split the note.`);
      setSnackOpen(true);
      return;
    }

    if (noteType === "") {
      setError({
        ...error,
        isNoteType: true
      });
    }
    if (updatedMessageText !== "" && noteType !== "" && !isLoading) {
      try {
        setIsLoading(true);
        const data = {
          personId: patientDetails?.userId,
          note: updatedMessageText,
          noteType: noteType,
          referenceId: referenceId,
          referenceType: referenceType,
          status: status === "release" ? "PUBLISHED" : "DRAFT",
          visibility: noteVisibility,
          authorId: authorId,
          authorName: authorName
        };
        const res =
          !chartingNoteId || isClone
            ? await ApiService.createChartingNotes(data)
            : await ApiService.updateChartingNotes(chartingNoteId, {
                ...data,
                id: chartingNoteId
              });

        if (res.data) {
          setSnackSeverity("success");
          setSnackText("Notes saved successfully");
          setSnackOpen(true);
          setIsLoading(false);
          if (status === "draft") {
            setLastSaveTime(new Date());
          }
          if (fetchChartNotes && typeof fetchChartNotes === "function") {
            fetchChartNotes();
          }
          if (cb) cb();
          setTimeout(() => {
            handleClearAndClose();
          }, 2000);
        }
      } catch (error) {
        console.log("error", error);
        setSnackSeverity("error");
        setSnackText(error?.response?.data?.reason || "Something went wrong");
        setSnackOpen(true);
        setIsLoading(false);
      }
    }
  };

  const submitSaveDraft = async () => {
    await saveOrReleaseNotes("draft");
  };

  const submitReleaseNotes = async () => {
    await saveOrReleaseNotes("release");
  };

  const handleTemplateApplication = async des => {
    try {
      const clinicianName = await getLoggedInPersonName();
      if (!patientDetails?.firstName || !patientDetails?.lastName) {
        window.confirm("There was an error fetching Patient's Name Automatically, please go ahead and manually enter patient name in the message.");
      }

      let message = des?.replace(/{{patientName}}/g, `${patientDetails?.firstName} ${patientDetails?.lastName}`);
      message = message?.replace(/{{clinicianName}}/g, clinicianName);
      setMessageText(message);
      setUpdatedMessageText(message);
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleClearAndClose = () => {
    setMessageText("");
    setUpdatedMessageText("");
    setTestType();
    onHide();
  };

  const resetTemplate = () => {
    setTestType();
    setMessageText("");
    setUpdatedMessageText("");
  };
  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const [isSidebarView, setIsSidebarView] = useState(false);

  const toggleView = () => {
    setIsSidebarView(!isSidebarView);
  };
  const modalClass = isSidebarView ? "sidebarViewModal" : "normalViewModal";

  return (
    <Modal show={show} size="xl" centered className={modalClass}>
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </Alert>
      </Snackbar>
      <Form id={"sendMessageForm"}>
        <Modal.Header id="modalHeader">
          <Modal.Title>Charting Notes</Modal.Title>
          <AiOutlineClose onClick={handleClearAndClose} style={{ position: "absolute", top: 20, right: 10, cursor: "pointer" }} size={30} />
          {/* <Button onClick={toggleView} style={{ position: "absolute", top: 10, right: 10 }}>
            {isSidebarView ? "Full View" : "Sidebar View"}
          </Button> */}
        </Modal.Header>
        <>
          <Modal.Body id="modalBody" className={isSidebarView && "sidebarViewModal-body "}>
            <Form.Group controlId="formLink">
              <Container fluid>
                <Row>
                  {/* First Column */}
                  <Col md={8} sm={12}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        marginBottom: 10,
                        flexDirection: "row"
                      }}
                    >
                      <Form.Select
                        value={testType?.name || ""}
                        onChange={e => {
                          const test = templatesList?.find(item => item.name === e.target.value);
                          setTestType(test);
                          handleTemplateApplication(test?.description);
                        }}
                        style={{ width: "35%", height: 30, borderRadius: 5, background: "white", marginTop: 10 }}
                      >
                        <option value="" default disabled>
                          Templates
                        </option>
                        {templatesList?.map((test, ind) => {
                          return (
                            <option key={ind} value={test.name}>
                              {test.name}
                            </option>
                          );
                        })}
                      </Form.Select>
                      {testType && (
                        <div style={{ cursor: "pointer", marginLeft: 10, textAlign: "center" }} onClick={resetTemplate}>
                          <u>Reset</u>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        marginTop: 20,
                        marginBottom: 5
                      }}
                    >
                      <b>Note</b>
                    </div>
                    {/* <TextArea messageText={messageText} setMessageText={setUpdatedMessageText} isErrorMessage={error.isMessage} /> */}
                    <div>
                      <style>
                        {`
                          .ql-editor {
                             min-height: 300px; 
                             max-height:300px;
                          }
                      `}
                      </style>

                      <ReactQuill
                        theme="snow"
                        value={updatedMessageText}
                        onChange={value => {
                          setUpdatedMessageText(value);
                        }}
                      />
                    </div>
                    {/* <Form.Control
                      style={{ backInline pasted images detected. Please remove images or upload them separately. (Base64 images are not allowed.)
ground: "rgba(0,0,0,0.05)" }}
                      as="textarea"
                      rows={12}
                      value={messageText}
                      onChange={e => {
                        setMessageText(e.target.value);
                        setError({
                          ...error,
                          isMessage: false
                        });
                      }}
                      className={`${error.isMessage && "error-select"} `}
                    /> */}
                  </Col>
                  <Col
                    md={4}
                    sm={12}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between"
                    }}
                  >
                    <div>
                      {" "}
                      <b>Patient Demographic Details</b>
                      <div style={{ textAlign: "left" }}>
                        Name : &nbsp; {patientDetails?.firstName} {patientDetails?.lastName}
                      </div>
                      <div style={{ textAlign: "left" }}>Email : &nbsp; {patientDetails?.email}</div>
                      <div style={{ textAlign: "left" }}>Phone : &nbsp; {patientDetails?.phone || patientDetails?.phoneNumber}</div>
                      <div style={{ textAlign: "left" }}>State : &nbsp; {patientDetails?.state || patientDetails?.address?.region}</div>
                      <div style={{ textAlign: "left" }}>DOB : &nbsp; {patientDetails?.dob}</div>
                      <div style={{ textAlign: "left" }}>Sex Assigned : &nbsp; {patientDetails?.genderAtBirth}</div>
                    </div>
                    {chartDetail && chartDetail.status !== "PUBLISHED" && (
                      <div>
                        {" "}
                        <b>Draft Details</b>
                        <div style={{ textAlign: "left" }}>Author Name : &nbsp; {chartDetail?.authorName}</div>
                        <div style={{ textAlign: "left" }}>Created Date : &nbsp; {convertEpochToDate(chartDetail?.createdEpoch)}</div>
                        {/* <div style={{ textAlign: "left" }}>Last Updated Date : &nbsp; {patientDetails?.phone || patientDetails?.phoneNumber}</div> */}
                      </div>
                    )}
                    <Row>
                      <Col>
                        <b>Note Type</b>
                        <Form.Group controlId="noteType">
                          <Form.Control
                            as="select"
                            value={noteType}
                            onChange={e => {
                              setNoteType(e.target.value);
                              setError({
                                ...error,
                                isNoteType: false
                              });
                            }}
                            required
                            className={`${error.isNoteType && "error-select"} `}
                            disabled={chartDetail?.noteType === "PATIENT_QUESTIONNAIRE_RESPONSE"}
                          >
                            <option value="">Select Note Type</option>
                            <option value="CLINICIAN_NOTE">Clinician Note</option>
                            <option value="APPOINTMENT_NOTE">Appointment Note</option>
                            <option value="REGISTERED_NURSE_NOTE">Registered Nurse Note</option>
                            <option value="ANCILLARY_STAFF_NOTE">Ancillary Staff Note</option>
                            {chartDetail?.noteType === "PATIENT_QUESTIONNAIRE_RESPONSE" && (
                              <option value="PATIENT_QUESTIONNAIRE_RESPONSE">Patient Questionnaire Response</option>
                            )}

                            {/* Add other options if needed */}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="mt-2">
                        <Form.Check
                          type="checkbox"
                          label="Make this note available to patient"
                          checked={noteVisibility === "EXTERNAL_NOTE"}
                          onChange={e => {
                            setNoteVisibility(e.target.checked ? "EXTERNAL_NOTE" : "INTERNAL_NOTE");
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Container>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Col>
              <Row
                style={{
                  justifyContent: "flex-end"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexDirection: "column"
                  }}
                >
                  {noteVisibility === "EXTERNAL_NOTE" && (
                    <p
                      style={{
                        color: "red"
                      }}
                    >
                      You are sending this notes to patient
                    </p>
                  )}
                  {lastSaveTime && (
                    <p
                      style={{
                        marginLeft: "10px",
                        color: "green",
                        marginTop: "-15px"
                      }}
                    >
                      Draft saved as of {lastSaveTime.toLocaleDateString()} {lastSaveTime.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </Row>
              <Row
                style={{
                  justifyContent: "flex-end",
                  gap: 20
                }}
              >
                <Button variant="light" type="button" onClick={handleClearAndClose}>
                  Close
                </Button>
                <Button id="msg_reply" class="replyButton sendButton text-right" onClick={() => submitSaveDraft()} disabled={isLoading}>
                  {!isLoading ? (
                    "Save Draft"
                  ) : (
                    <div
                      className="loading"
                      style={{
                        margin: "5px 20px",
                        height: "20px",
                        width: "20px"
                      }}
                    />
                  )}
                </Button>
                <Button id="msg_reply" class="replyButton sendButton text-right " onClick={() => submitReleaseNotes()} disabled={isLoading}>
                  {!isLoading ? (
                    <>
                      Submit <i className="fa fa-paper-plane" aria-hidden="true"></i>
                    </>
                  ) : (
                    <div
                      className="loading"
                      style={{
                        margin: "5px 20px",
                        height: "20px",
                        width: "20px"
                      }}
                    />
                  )}
                </Button>
              </Row>
            </Col>
          </Modal.Footer>
        </>
      </Form>
    </Modal>
  );
}

export default CreateChartNotesModal;
